<script setup lang="ts">
  import {useI18n} from 'vue-i18n';
  import useCpBem from '~/composables/useCpBem';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import CpIconStandard from '~/components/cp/CpIcon/CpIconStandard/CpIconStandard.vue';

  defineProps<{
    ratings: Array<{ stars: number, total: number }>,
    totalRate: number,
    shownReviews: number,
    reviewsNumber: number,
    af?: boolean
  }>();
  const {t} = useI18n();
  const {b, e} = useCpBem('cp-card-review-rating');
</script>

<template>
    <div :class="b">
        <div :class="e('top-row')">
            <div :class="e('item-row')">
                <CpText variant="heading-h1-fake" :type="af ? 'warning-dark' : 'secondary-dark'">
                    {{ totalRate }}
                </CpText>
                <CpIconStandard
                    :class="e('star-icon')"
                    kind="standard"
                    icon="star_rate"
                    :type="af ? 'warning' : 'secondary'"
                />
            </div>
            <CpText variant="caption-regular" :type="af ? 'neutral-extra-light' : 'neutral'">
                {{ t('fiveStars') }}
            </CpText>
        </div>
        <div :class="e('bottom-row')">
            <div v-for="rate in ratings" :key="rate.stars" :class="e('item-row')">
                <div :class="e('stars')">
                    <CpText variant="caption-bold" type="primary" :class="e('number')">
                        {{ rate.stars }}
                    </CpText>
                    <CpIconStandard
                        :class="e('star-icon')"
                        kind="small"
                        icon="star_rate"
                        :type="af ? 'warning' : 'secondary'"
                    />
                </div>
                <v-progress-linear
                    :class="e('progress-linear')"
                    :model-value="(rate.total * 100).toFixed(0)"
                    :bg-color="af ? '#363650' : '#EEEEEE'"
                    :color="af ? '#00C0CB' : '#0066BB'"
                    rounded="8"
                    height="8"
                    location="left bottom"
                    rounded-bar
                    bg-opacity="1"
                />
                <div :class="e('average-container')">
                    <CpText variant="caption-bold" :type="af ? 'white' : 'neutral'">
                        {{ (rate.total * 100).toFixed(0) }}%
                    </CpText>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
  .cp-card-review-rating {
    display: flex;
    flex-direction: row;
    gap:  var(--cp-gutter);
    padding: var(--cp-gutter);
    border-bottom: var(--cp-border-standard);

    &__item_row {
      display: flex;
      gap: var(--cp-gutter-xsmall);
      align-items: center;
    }

    &__top-row {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
    }
    &__bottom-row {
      display: flex;
      flex-direction: column;
      gap: var(--cp-gutter-xsmall);
      flex: 1;
    }
    &__item-row {
      display: flex;
      gap: var(--cp-gutter-xsmall);
      align-items: center;
    }
    &__stars {
      display: flex;
      align-items: center;
    }
    &__progress-linear {
      display: block;
      align-items: center;
      flex: 1;
    }
    &__average-container {
      display: flex;
      justify-content: flex-start;
      min-width: 35px;
    }
    &__number {
      width: var(--cp-gutter-small);
    }
  }
</style>

<i18n locale="es-US" lang="yaml">
  fiveStars: 'de 5 estrellas'
</i18n>
